import Cookies from "js-cookie";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AWS_URL } from "../../Constant";

const AddExpenses = () => {
  const initialFormData = {
    date: "",
    item: "",
    amount: "",
    description: "",
  };

  const expenseItems = [
    "Office Maintenance",
    "Marketing Expense",
    "Finance Charges",
    "Courier and Postage",
    "Stationery",
    "Travelling Expenses",
    "Repairs and Maintance",
    "Transportation Expenses",
    "Incidental Expenses",
    "Service Expenses",
    "Other",
  ];

  const [formData, setFormData] = useState(initialFormData);
  const [error, setError] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // single state for the selected file
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);

  // cleanup preview URL when component unmounts or image changes
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  // ✅ Validation
  const validateForm = () => {
    let err = {};
    if (!formData.date) err.date = "Date is required";
    if (!formData.item || formData.item.trim() === "") err.item = "Item is required";
    if (!formData.amount && formData.amount !== 0) err.amount = "Amount is required";
    if (!formData.description || formData.description.trim() === "")
      err.description = "Description is required";
    if (!image) err.image = "Image is required";

    setError(err);
    return Object.keys(err).length === 0;
  };

  // single file handler (works for file select and camera capture)
  const handleFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      setImage(null);
      if (preview) {
        URL.revokeObjectURL(preview);
        setPreview(null);
      }
      return;
    }

    // revoke previous preview URL if any
    if (preview) URL.revokeObjectURL(preview);

    setImage(file);
    setPreview(URL.createObjectURL(file));
  };

  // Handle Input Change
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "amount" ? (value === "" ? "" : parseFloat(value)) : value,
    }));
  };

  // Handle Submit
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setMessage(null);

    const jwtToken = Cookies.get("jwt_token");
    const url = `${AWS_URL}/addexpenses`;

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("date", formData.date);
      formDataToSend.append("item", formData.item);
      formDataToSend.append("amount", formData.amount);
      formDataToSend.append("description", formData.description);
      formDataToSend.append("image", image); // attach the file

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${jwtToken}`, // don't set Content-Type
        },
        body: formDataToSend,
      });

      const data = await response.json();
      if (response.ok) {
        setMessage({ type: "success", text: "Expense added successfully ✅" });
        setFormData(initialFormData);
        // clear file + preview
        setImage(null);
        if (preview) {
          URL.revokeObjectURL(preview);
          setPreview(null);
        }
      } else {
        setMessage({
          type: "error",
          text: data?.message || data?.error || "Failed to add expense ❌",
        });
      }
    } catch (err) {
      setMessage({
        type: "error",
        text: "Something went wrong. Please try again ❌",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-slate-100 min-h-screen p-5">
      {/* Dashboard Button */}
      <div className="flex justify-end">
        <Link to={"/dashboard"}>
          <button className="mt-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 px-4 py-2 rounded-lg text-white text-lg shadow-md hover:opacity-90">
            Dashboard
          </button>
        </Link>
      </div>

      {/* Card */}
      <div className="flex flex-col items-center mt-8">
        <div className="bg-white rounded-2xl shadow-lg p-8 w-full sm:w-8/12 md:w-6/12">
          <h1 className="text-xl font-bold text-center mb-6 bg-gradient-to-r from-cyan-500 to-blue-500 text-white py-2 rounded-lg">
            Add Expense
          </h1>

          {/* Form */}
          <form className="space-y-5" onSubmit={handleSubmit}>
            {/* Date */}
            <div>
              <label htmlFor="date" className="block font-medium">Date</label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                className="border px-3 py-2 rounded-lg w-full focus:ring-2 focus:ring-blue-400"
              />
              {error.date && <p className="text-red-500 text-sm mt-1">{error.date}</p>}
            </div>

            {/* Item */}
            <div>
              <label htmlFor="item" className="block font-medium">Item</label>
              <select
                id="item"
                name="item"
                value={formData.item}
                onChange={handleInputChange}
                className="border px-3 py-2 rounded-lg w-full focus:ring-2 focus:ring-blue-400"
                required
              >
                <option value="">Select Item</option>
                {expenseItems.map((item, index) => (
                  <option key={index} value={item}>{item}</option>
                ))}
              </select>
              {error.item && <p className="text-red-500 text-sm mt-1">{error.item}</p>}
            </div>

            {/* Amount */}
            <div>
              <label htmlFor="amount" className="block font-medium">Amount</label>
              <input
                type="number"
                id="amount"
                name="amount"
                value={formData.amount}
                placeholder="Enter amount"
                onChange={handleInputChange}
                className="border px-3 py-2 rounded-lg w-full focus:ring-2 focus:ring-blue-400"
              />
              {error.amount && <p className="text-red-500 text-sm mt-1">{error.amount}</p>}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block font-medium">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                placeholder="Enter description"
                onChange={handleInputChange}
                className="border px-3 py-2 rounded-lg w-full focus:ring-2 focus:ring-blue-400"
              />
              {error.description && <p className="text-red-500 text-sm mt-1">{error.description}</p>}
            </div>

            {/* Single File Input (works as camera on mobile) */}
            <div>
              <label htmlFor="image" className="block font-medium">Upload Bill/Receipt</label>
              <input
                type="file"
                id="image"
                name="image"
                accept="image/*"
                // capture="environment" // opens camera on mobile
                // capture
                onChange={handleFileChange}
                className="border px-3 py-2 rounded-lg w-full focus:ring-2 focus:ring-blue-400"
              />
              {error.image && <p className="text-red-500 text-sm mt-1">{error.image}</p>}

              {preview && (
                <img
                  src={preview}
                  alt="preview"
                  className="mt-2 w-32 h-32 object-cover border rounded-lg"
                />
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full py-2 rounded-lg text-white font-medium transition 
                ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 hover:opacity-90 shadow-md"}`}
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
          </form>

          {/* Feedback Message */}
          {message && (
            <p className={`mt-4 text-center font-medium ${message.type === "success" ? "text-green-600" : "text-red-600"}`}>
              {message.text}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddExpenses;
